// A18 — FAQ Templates (ticket question templates). Same TVP2 §9 styling
// as JobCategoriesList: bordered Search filter card → bordered action
// bar + table card. Bootstrap palette (Search blue · Add green ·
// Reset/Delete grey). Add/Edit happens in a Dialog. Delete is a HARD
// delete (the templates table has no FK dependents).

import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";
import { usePagedQuery } from "@/hooks/usePagedQuery";

interface FaqTemplateRow {
  id: string;
  category: string | null;
  question: string;
  order_idx: number;
}

interface FormState {
  id?: string;
  category: string;
  question: string;
  order_idx: number;
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "bad_request":
      return "Invalid input. Double-check the form and try again.";
    default:
      return err.message || fallback;
  }
}

export function FaqTemplatesList() {
  const { show, ToastEl } = useToast();

  const [categoryQ, setCategoryQ] = useState("");
  const [filters, setFilters] = useState<{ category?: string }>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState<FormState | null>(null);

  const list = usePagedQuery<FaqTemplateRow>("ticket-question-templates", {
    filters,
    initialPagination: { page: 0, pageSize: 10 },
  });

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setFilters({ category: categoryQ.trim() || undefined });
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };
  const onReset = () => {
    setCategoryQ("");
    setFilters({});
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const body = {
        category: editing.category || undefined,
        question: editing.question,
        order_idx: editing.order_idx,
      };
      if (editing.id) {
        await apiFetch(`/v1/admin/ticket-question-templates/${editing.id}`, {
          method: "PATCH",
          body,
        });
      } else {
        await apiFetch("/v1/admin/ticket-question-templates", {
          method: "POST",
          body,
        });
      }
    },
    onSuccess: () => {
      show("Saved", "success");
      setEditing(null);
      list.invalidate();
    },
    onError: (err) => show(friendlyError(err, "Save failed"), "error"),
  });

  // Bulk hard-delete. Templates have no FK dependents (see comment in
  // sub-plan 17 §17.4), so DELETE is permanent.
  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results: Array<{ id: string; ok: boolean }> = [];
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]!;
        try {
          await apiFetch(`/v1/admin/ticket-question-templates/${id}`, {
            method: "DELETE",
          });
          results.push({ id, ok: true });
        } catch {
          results.push({ id, ok: false });
        }
        if (i < ids.length - 1) await new Promise((r) => setTimeout(r, 200));
      }
      return results;
    },
    onSuccess: (results) => {
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.length - okCount;
      show(
        failCount === 0
          ? `Deleted ${okCount} of ${results.length}`
          : `Deleted ${okCount} of ${results.length}; ${failCount} failed`,
        failCount === 0 ? "success" : "warning",
      );
      setSelected([]);
      setDeleteOpen(false);
      list.invalidate();
    },
  });

  const columns: DataTableColumn<FaqTemplateRow>[] = [
    {
      field: "category",
      headerName: "Category",
      render: (r) => r.category ?? "-",
    },
    { field: "question", headerName: "Question" },
    {
      field: "order_idx",
      headerName: "Order",
      align: "right",
    },
  ];

  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const searchBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
  };
  const addBtn = {
    ...baseBtn,
    backgroundColor: "#5CB85C",
    "&:hover": { backgroundColor: "#4CAE4C", boxShadow: "none" },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#E8E8E8",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const primaryBtnDialog = {
    ...baseBtn,
    px: 3,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Search filter card */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
        }}
      >
        <Tabs
          value="search"
          sx={{
            minHeight: 38,
            borderBottom: `1px solid ${tokens.colors.admin.border}`,
            "& .MuiTabs-indicator": {
              backgroundColor: tokens.colors.admin.tabActive,
              height: 2,
            },
            "& .MuiTab-root": {
              minHeight: 38,
              textTransform: "none",
              fontSize: 13,
              color: tokens.colors.admin.textHeading,
              "& .MuiTab-iconWrapper": {
                marginRight: 0.5,
                marginBottom: "0 !important",
                "& svg": { fontSize: 16 },
              },
            },
          }}
        >
          <Tab
            value="search"
            iconPosition="start"
            icon={<SearchIcon />}
            label="Search"
          />
        </Tabs>
        <Box component="form" onSubmit={onSearch} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="Category"
              variant="standard"
              fullWidth
              helperText="Exact match (e.g. 'general', 'billing')"
              value={categoryQ}
              onChange={(e) => setCategoryQ(e.target.value)}
            />
          </Box>
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" sx={searchBtn}>
              Search
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={onReset}
              sx={greyBtn}
            >
              Reset
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Action bar + table card */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack direction="row" spacing={2} sx={{ px: 3, pt: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={addBtn}
            onClick={() =>
              setEditing({ category: "", question: "", order_idx: 0 })
            }
          >
            Add
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            disabled={selected.length === 0 || deleteMutation.isPending}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Stack>

        <Box sx={{ px: 3, pb: 2 }}>
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            rowCount={list.data?.total ?? 0}
            paginationModel={list.paginationModel}
            onPaginationModelChange={list.setPaginationModel}
            getRowId={(r) => r.id}
            checkboxSelection
            selectedIds={selected}
            onSelectionChange={setSelected}
            loading={list.isLoading}
            bordered={false}
            rowActions={(row) => (
              <Stack direction="row" spacing={0}>
                <IconButton
                  size="small"
                  aria-label="Edit"
                  onClick={() =>
                    setEditing({
                      id: row.id,
                      category: row.category ?? "",
                      question: row.question,
                      order_idx: row.order_idx,
                    })
                  }
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Delete"
                  onClick={() => {
                    setSelected([row.id]);
                    setDeleteOpen(true);
                  }}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          />
        </Box>
      </Paper>

      {/* Add / Edit dialog */}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editing?.id ? "Edit FAQ Template" : "Add FAQ Template"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Category"
              variant="standard"
              fullWidth
              inputProps={{ maxLength: 80 }}
              value={editing?.category ?? ""}
              onChange={(e) =>
                setEditing((s) =>
                  s ? { ...s, category: e.target.value } : s,
                )
              }
            />
            <TextField
              label="Question"
              variant="standard"
              required
              fullWidth
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              value={editing?.question ?? ""}
              onChange={(e) =>
                setEditing((s) =>
                  s ? { ...s, question: e.target.value } : s,
                )
              }
            />
            <TextField
              label="Order"
              variant="standard"
              type="number"
              fullWidth
              helperText="Lower numbers appear first in the customer mobile picker"
              value={editing?.order_idx ?? 0}
              onChange={(e) =>
                setEditing((s) =>
                  s
                    ? { ...s, order_idx: Number(e.target.value) || 0 }
                    : s,
                )
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={{ ...greyBtn, px: 3 }}
            onClick={() => setEditing(null)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!editing?.question || save.isPending}
            sx={primaryBtnDialog}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={deleteOpen}
        title="Delete templates"
        body={`About to permanently delete ${selected.length} template${selected.length === 1 ? "" : "s"}. This cannot be undone. Continue?`}
        confirmLabel="Delete"
        danger
        pending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(selected)}
        onCancel={() => setDeleteOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}
